import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import CreateProductionPurposeModal from '../../Modals/CreateProductionPurposeModal';
import EditProductionPurposeModal from '../../Modals/EditProductionPurposeModal';
import RecordDetailsModal from '../../shared/RecordDetailsModal';
import { productionPurposeApi } from '../../../../api/speciesLivestockRegistryApi';
import {
  actionMenuHtml,
  boolBadgeHtml,
  humanize,
  parseApiError,
  textCellHtml,
} from './registryHelpers';
import RegistryToolbar from './RegistryToolbar';
import { useSpeciesCatalog } from './useSpeciesCatalog';
import SystemRowMenu, { standardRowItems, confirmAction } from '../../shared/SystemRowMenu';
import { anchorRect } from '../../../shared/useAnchoredMenu';

const COLUMNS = [
  { title: 'Species Code',   data: 'species_code',   sortable: true, render: textCellHtml },
  { title: 'Purpose Code',   data: 'purpose_code',   sortable: true, render: textCellHtml },
  { title: 'Purpose Label',  data: 'purpose_label',  sortable: true, render: textCellHtml },
  { title: 'Seasonal Flag',  data: 'seasonal_flag',  sortable: true, render: boolBadgeHtml },
  { title: 'Action', data: null, sortable: false, render: (_d, type) => (type === 'display' ? actionMenuHtml() : '') },
];

export default function ProductionPurposeRegistry() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [error, setError] = useState('');
  const { species, error: speciesError, refresh: retrySpecies } = useSpeciesCatalog();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    productionPurposeApi.listAll({ per_page: 200 })
      .then(({ items }) => { if (active) setRecords(items); })
      .catch((err) => { if (active) setError(parseApiError(err, 'Failed to load production purposes.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshTick]);

  const handleSearch = () => setSearchQuery(searchDraft.trim());
  const handleRefresh = () => setRefreshTick((v) => v + 1);

  const handleCreate = async (payload) => {
    await productionPurposeApi.create(payload);
    toast.success('Production purpose created.', { position: 'top-right' });
    handleRefresh();
  };

  const handleSave = async (payload) => {
    if (!payload?.uuid) throw new Error('Missing production purpose identifier.');
    await productionPurposeApi.update(payload.uuid, payload);
    toast.success('Production purpose updated.', { position: 'top-right' });
    handleRefresh();
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const label = row.purpose_label || row.purpose_code || 'this purpose';
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete ${label}?`, confirmLabel: 'Delete', danger: true }))) return;
    setDeleting(true);
    try {
      await productionPurposeApi.remove(row.uuid);
      setViewRow(null);
      toast.success('Production purpose removed.', { position: 'top-right' });
      handleRefresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Unable to delete production purpose.'), { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'edit') setEditRow(row);
  };

  return (
    <section>
      <RegistryToolbar
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create New"
      />

      {speciesError && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{speciesError}</span>
            <button type="button" onClick={retrySpecies}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <button type="button" onClick={handleRefresh}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && (
        <p className="mb-3 text-sm text-gray-500">Loading production purposes...</p>
      )}

      <NlimsDataTable
        tableId="production-purpose-table"
        columns={COLUMNS}
        data={records}
        serverSide={false}
        searchQuery={searchQuery}
        hideSearch
        pageLength={10}
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => { setEditRow(viewRow); setViewRow(null); }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Production Purpose Details"
        subtitle={viewRow?.purpose_label || viewRow?.purpose_code}
        fields={viewRow ? [
          { label: 'Species Code',  value: viewRow.species_code || '-' },
          { label: 'Purpose Code',  value: viewRow.purpose_code || '-' },
          { label: 'Purpose Label', value: viewRow.purpose_label || '-' },
          { label: 'Seasonal Flag', value: viewRow.seasonal_flag ? 'Yes' : 'No' },
          { label: 'UUID', value: viewRow.uuid, mono: true, wide: true },
        ] : []}
      />

      <CreateProductionPurposeModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        species={species}
      />

      <EditProductionPurposeModal
        isOpen={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        row={editRow}
        species={species}
      />
    </section>
  );
}
